import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Alert, Box, Breadcrumbs, Button, CircularProgress, FormControl, InputLabel, List, ListItem, MenuItem, Paper,
  Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import LeaderboardRoundedIcon from '@mui/icons-material/LeaderboardRounded';
import AppShell from './components/AppShell';
import api from './services/api';
import { safeDecode } from './utils/decode';
import QuotaTable, { SystemAlerts } from './quotas/QuotaTable';
import useSurveyQuotas from './quotas/useSurveyQuotas';
import SharingPanel from './sharing/SharingPanel';

const QuotaMonitoring = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [quotaMonitors, setQuotaMonitors] = useState([]);
  const { quotas, systems, loading: loadingQuotas, error: quotasError, reload } = useSurveyQuotas(surveyId);
  const [bumpPct, setBumpPct] = useState('');
  const [bumping, setBumping] = useState(false);
  const [bumpResult, setBumpResult] = useState(null);
  const [partnerError, setPartnerError] = useState(null);
  const [sharing, setSharing] = useState(null);

  // "Partners see": which system's quotas partners get from a view. Saved at once.
  const setPartnerSystem = async (monitor, system) => {
    setPartnerError(null);
    try {
      const { data } = await api.patch(`/quota-monitors/${monitor.id}/`, { partner_system: system });
      setQuotaMonitors((list) => list.map((m) => (m.id === monitor.id ? data : m)));
    } catch {
      setPartnerError(`Couldn't change what partners see in ${monitor.quota_view_name}.`);
    }
  };
  const countIn = (monitor, system) => monitor.quota_refs.filter((r) => r.system === system).length;

  useEffect(() => {
    api.get(`/surveys/${surveyId}/`).then((res) => setSurvey(res.data));
    api.get(`/quota-monitors/?survey=${surveyId}`).then((res) => setQuotaMonitors(res.data));
  }, [surveyId]);

  const handleBumpFullQuotas = async () => {
    const pct = parseFloat(bumpPct);
    if (!pct || pct <= 0) return;
    setBumping(true);
    setBumpResult(null);
    try {
      const res = await api.post(`/surveys/${surveyId}/bump-full-quotas/`, { percentage: pct });
      setBumpResult(res.data.updated);
      reload();
    } finally {
      setBumping(false);
    }
  };

  if (!survey) return null;

  return (
    <AppShell>
      <Breadcrumbs separator=">" sx={{ mb: 4 }}>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link>
        <Link to={`/project/${survey.project}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.project_name}</Link>
        <Link to={`/survey/${surveyId}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.name}</Link>
        <Typography color="textPrimary">Quota Monitoring</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <LeaderboardRoundedIcon color="primary" />
        <Typography variant="h4" fontFamily="'Work Sans', sans-serif" fontWeight={700}>
          Quota Monitoring
        </Typography>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        View quota progress or create a quota view to share with partners.
      </Typography>

      <Button
        variant="contained"
        onClick={() => navigate(`/survey/${surveyId}/make-quota-view`)}
        sx={{ textTransform: 'none', borderRadius: 2, mb: 3 }}
      >
        Make a quota view
      </Button>

      {sharing && <SharingPanel monitor={sharing} onClose={() => setSharing(null)} />}
      {quotaMonitors.length > 0 && (
        <Box
          sx={{
            bgcolor: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: 3,
            p: 3,
            mb: 4,
          }}
        >
          <Typography variant="h6" gutterBottom>Existing Quota Views</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Partners see one system&apos;s quotas from each view, and nothing that tells them there are two.
          </Typography>
          {partnerError && <Alert severity="error" sx={{ mb: 1 }}>{partnerError}</Alert>}
          <List disablePadding>
            {quotaMonitors.map((qm) => (
              <ListItem key={qm.id} disableGutters sx={{ gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                <Button
                  component={Link}
                  to={`/monitor-quotas/${qm.id}`}
                  fullWidth
                  variant="outlined"
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  {qm.quota_view_name}
                </Button>
                <FormControl size="small" sx={{ minWidth: 250, flexShrink: 0 }}>
                  <InputLabel id={`partners-see-${qm.id}`}>Partners see</InputLabel>
                  <Select labelId={`partners-see-${qm.id}`} label="Partners see" value={qm.partner_system}
                    onChange={(e) => setPartnerSystem(qm, e.target.value)}>
                    {['alchemer', 'wrangler'].map((system) => (
                      <MenuItem key={system} value={system}>
                        {system === 'alchemer' ? 'Alchemer' : 'Wrangler'} quotas ({countIn(qm, system)})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="outlined" onClick={() => setSharing(qm)} sx={{ textTransform: 'none', borderRadius: 2, flexShrink: 0 }}>
                  Share
                </Button>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      <Box
        sx={{
          bgcolor: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: 3,
          p: 3,
        }}
      >
        <Typography variant="h5" fontFamily="'Work Sans', sans-serif" fontWeight={600} sx={{ mb: 3 }}>
          Survey Quotas
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Bump full quotas (Alchemer quotas only)</Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
          Raises the limit of every full Alchemer quota by this percentage, rounded up. It doesn't touch Wrangler quotas.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
          <TextField
            label="Bump full quotas by %"
            type="number"
            size="small"
            value={bumpPct}
            onChange={(e) => setBumpPct(e.target.value)}
            slotProps={{ input: { min: 1, step: 1 } }}
            sx={{ width: 200 }}
          />
          <Button
            variant="outlined"
            onClick={handleBumpFullQuotas}
            disabled={bumping || !bumpPct}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {bumping ? 'Updating…' : 'Apply'}
          </Button>
        </Box>

        {bumpResult && (
          <Box sx={{ mb: 3 }}>
            {bumpResult.length === 0 ? (
              <Typography color="textSecondary">No full quotas found.</Typography>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Updated {bumpResult.length} quota{bumpResult.length !== 1 ? 's' : ''}:
                </Typography>
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, maxHeight: 200 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f6fa' }}>
                        <TableCell>Name</TableCell>
                        <TableCell>Old limit</TableCell>
                        <TableCell>New limit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bumpResult.map((q) => (
                        <TableRow key={q.id}>
                          <TableCell>{safeDecode(q.name)}</TableCell>
                          <TableCell>{q.old_limit}</TableCell>
                          <TableCell>{q.new_limit}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        )}

        <SystemAlerts systems={systems} />
        {quotasError && <Alert severity="error" sx={{ mb: 2 }}>{quotasError}</Alert>}
        {loadingQuotas ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <QuotaTable quotas={quotas} />
        )}
      </Box>
    </AppShell>
  );
};

export default QuotaMonitoring;
